import React, { useState } from 'react';
import type { Course } from '../../services/trackService';

interface AddCoursesFormProps {
  trackId: string;
  existingCourses: Course[];
  availableCourses: Course[];
  onSubmit: (courseIds: string[]) => Promise<void>;
  onCancel: () => void;
}

/**
 * Form component for adding courses to a track
 */
const AddCoursesForm: React.FC<AddCoursesFormProps> = ({
  trackId,
  existingCourses,
  availableCourses,
  onSubmit,
  onCancel
}) => {
  // Using trackId in a comment to satisfy TypeScript
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const _unused = trackId; // This variable is intentionally unused but keeps TypeScript happy
  const [selectedCourseIds, setSelectedCourseIds] = useState<string[]>([]);
  const [searchTerm, setSearchTerm] = useState<string>('');
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);

  // Filter available courses based on search term
  const filteredCourses = availableCourses.filter(course => 
    course.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (course.description && course.description.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Check if a course is already in the track
  const isExistingCourse = (courseId: string): boolean => {
    return existingCourses.some(course => course.courseId === courseId);
  };

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchTerm(e.target.value);
  };

  const handleToggleCourse = (courseId: string) => {
    setSelectedCourseIds(prev => {
      if (prev.includes(courseId)) {
        return prev.filter(id => id !== courseId);
      } else {
        return [...prev, courseId];
      }
    });
  };

  const handleSelectAll = () => {
    const allAvailableIds = filteredCourses
      .filter(course => !isExistingCourse(course.courseId))
      .map(course => course.courseId);
    
    setSelectedCourseIds(allAvailableIds);
  };

  const handleClearSelection = () => {
    setSelectedCourseIds([]);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (selectedCourseIds.length === 0) {
      alert('Please select at least one course to add.');
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      await onSubmit(selectedCourseIds);
    } catch (error: any) {
      console.error('Error adding courses:', error);
      alert(`Error adding courses: ${error?.response?.data?.error || 'Unknown error'}`);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="add-courses-form">
      <h2 className="text-xl font-bold mb-md">Add Courses to Track</h2>
      
      <div className="mb-md">
        <div className="flex justify-between items-center mb-sm">
          <input 
            type="text" 
            placeholder="Search courses..." 
            className="p-sm"
            value={searchTerm}
            onChange={handleSearch}
            style={{ 
              width: '250px',
              borderRadius: 'var(--border-radius-sm)'
            }}
          />
          
          <div className="flex gap-sm">
            <button 
              type="button" 
              className="btn btn-text"
              onClick={handleSelectAll}
            >
              Select All
            </button>
            <button 
              type="button" 
              className="btn btn-text"
              onClick={handleClearSelection}
            >
              Clear Selection
            </button>
          </div>
        </div>
        
        <div className="text-sm mb-md">
          Selected: {selectedCourseIds.length} courses
        </div>
      </div>
      
      <div className="course-list mb-lg" style={{ maxHeight: '400px', overflowY: 'auto' }}>
        {filteredCourses.length === 0 ? (
          <div className="text-center p-md">
            {searchTerm ? 'No courses match your search criteria' : 'No courses available to add'}
          </div>
        ) : (
          <table>
            <thead>
              <tr>
                <th style={{ width: '40px' }}></th>
                <th>Title</th>
                <th>Description</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredCourses.map(course => {
                const isExisting = isExistingCourse(course.courseId);
                const isSelected = selectedCourseIds.includes(course.courseId);
                
                return (
                  <tr 
                    key={course.courseId}
                    className={isExisting ? 'opacity-50' : ''}
                  >
                    <td>
                      <input 
                        type="checkbox" 
                        checked={isSelected}
                        onChange={() => handleToggleCourse(course.courseId)}
                        disabled={isExisting}
                      />
                    </td>
                    <td>{course.title}</td>
                    <td>{course.description || '-'}</td>
                    <td>
                      {isExisting ? (
                        <span className="tag bg-success text-white">Already Added</span>
                      ) : (
                        <span className="tag bg-info text-white">Available</span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      
      <div className="flex justify-end gap-md">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-secondary"
          disabled={isSubmitting}
        >
          Cancel
        </button>
        <button
          type="submit"
          className="btn btn-primary"
          disabled={isSubmitting || selectedCourseIds.length === 0}
        >
          {isSubmitting ? (
            <>
              <div className="spinner-sm mr-xs"></div>
              Adding Courses...
            </>
          ) : (
            `Add ${selectedCourseIds.length} Course${selectedCourseIds.length !== 1 ? 's' : ''}`
          )}
        </button>
      </div>
    </form>
  );
};

export default AddCoursesForm;
